import useTenantMember from '@globals/useTenantMember'
import { TenantMember } from '@kudos/http-client'
import {
  getMentionPreviewRegionId,
  isPreviewableTenantMember,
} from '@shared/memberProfilePreview/memberProfilePreviewUtils'
import { useT } from '@transifex/react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { getPreviewEmbeds } from './htmlContentWithMentionsUtils'

const MENTION_A11Y_ATTR = 'data-mention-a11y'
// Navigation gate. Mentions start disabled and only become clickable once a
// preview fetch has verified the member still resolves. Also set while the
// preview is loading and left set permanently for deleted members.
export const MENTION_DISABLED_ATTR = 'data-mention-disabled'

type UseHtmlContentWithMentionsProps = {
  htmlContent: string
  onNavigate: (mentionId: string) => void
  richPreview?: boolean
  selfMemberId?: string | null
}

type UseHtmlContentWithMentionsReturn = {
  containerRef: React.RefObject<HTMLDivElement | null>
  anchorEl: HTMLElement | null
  tenantMemberId: string | null
  open: boolean
  closePreview: () => void
  member: TenantMember | null
  isFetched: boolean
  deletedMentionIds: ReadonlySet<string>
}

export const useHtmlContentWithMentions = ({
  htmlContent,
  onNavigate,
  richPreview = false,
  selfMemberId,
}: UseHtmlContentWithMentionsProps): UseHtmlContentWithMentionsReturn => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [tenantMemberId, setTenantMemberId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [deletedMentionIds, setDeletedMentionIds] = useState<ReadonlySet<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)
  const lastPreviewAnchorRef = useRef<HTMLElement | null>(null)
  const openRef = useRef(false)
  const tenantMemberIdRef = useRef<string | null>(null)
  const previewLoadingRef = useRef(false)
  const anchorElRef = useRef<HTMLElement | null>(null)
  const t = useT()
  // Read `t` via a ref inside the a11y setup so that effect keys off htmlContent
  // only. Otherwise an unstable `t` re-runs it every render, and its cleanup +
  // re-add would clobber attributes the deleted-mention effect strips.
  const tRef = useRef(t)

  openRef.current = open
  tenantMemberIdRef.current = tenantMemberId
  previewLoadingRef.current = previewLoading
  anchorElRef.current = anchorEl
  tRef.current = t

  const getMentionFromEvent = useCallback((event: Event): HTMLElement | null => {
    const container = containerRef.current
    if (!container) {
      return null
    }
    const target = event.target as HTMLElement
    const mention = target.closest<HTMLElement>('[data-type="mention"]')
    if (!mention || !container.contains(mention)) {
      return null
    }
    return mention
  }, [])

  const clearPreviewAria = useCallback((el: HTMLElement) => {
    el.removeAttribute('aria-describedby')
    el.removeAttribute('aria-expanded')
    el.removeAttribute('aria-busy')
  }, [])

  const closePreview = useCallback(() => {
    setOpen(false)
  }, [])

  const handleMemberDeleted = useCallback(
    (id: string) => {
      setDeletedMentionIds(prev => (prev.has(id) ? prev : new Set(prev).add(id)))
      // The deleted member is always the one being previewed (the fetch is for the
      // open mention), so close symmetrically for both the rich and legacy paths.
      closePreview()
    },
    [closePreview],
  )

  const openPreview = useCallback(
    (mention: HTMLElement) => {
      const mentionId = mention.getAttribute('data-id')
      // Deleted members have no preview to mount, so opening one would only set
      // aria-busy on a mention whose preview never renders.
      if (!mentionId || deletedMentionIds?.has(mentionId)) {
        return
      }
      // Never preview yourself in the rich (on-route) profile card.
      if (richPreview && mentionId === selfMemberId) {
        return
      }
      // Already previewing this exact mention while open: re-affirming it would
      // flip previewLoading back to true while the preview is already mounted and
      // won't re-report its loading state, leaving the gate stuck disabled. Once
      // closed, re-hovering the same mention should reopen it.
      if (anchorElRef.current === mention && openRef.current) {
        return
      }
      // Gate up front. previewLoading defaults to false and is only flipped true
      // by the preview fetch a tick later, so without setting it here the preview
      // effect would briefly observe "not loading" and clear the gate before
      // verification has even started (and stay cleared for a cached 404).
      setPreviewLoading(true)
      setAnchorEl(mention)
      setTenantMemberId(mentionId)
      setOpen(true)
    },
    [richPreview, selfMemberId, deletedMentionIds],
  )

  const handleMouseOver = useCallback(
    (event: Event) => {
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      event.stopPropagation()
      openPreview(mention)
    },
    [getMentionFromEvent, openPreview],
  )

  const handleMouseClick = useCallback(
    (event: Event) => {
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      event.stopPropagation()
      const mentionId = mention.getAttribute('data-id')
      if (!mentionId) {
        return
      }
      if (mention.getAttribute(MENTION_DISABLED_ATTR) === 'true') {
        const isDeleted = deletedMentionIds?.has(mentionId) ?? false
        const isVerifying = previewLoadingRef.current && tenantMemberIdRef.current === mentionId
        // A deleted member must never route to a profile (that shows the global
        // 404 page), and an in-flight verification should settle first. Any other
        // gated state means no preview ever opened to verify this mention — e.g.
        // a touch tap, where previews don't open — so fall back to navigating
        // directly, keeping mobile mentions usable (matches pre-gating behavior).
        if (isDeleted || isVerifying) {
          return
        }
      }
      onNavigate(mentionId)
    },
    [getMentionFromEvent, onNavigate, deletedMentionIds],
  )

  const handleFocusIn = useCallback(
    (event: Event) => {
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      event.stopPropagation()
      openPreview(mention)
    },
    [getMentionFromEvent, openPreview],
  )

  const handleFocusOut = useCallback(
    (event: Event) => {
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      event.stopPropagation()
    },
    [getMentionFromEvent],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return
      }
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      // A mention is role="button", so Enter/Space must never fall through to the
      // browser default (Space scrolls the page) or bubble to parents.
      event.preventDefault()
      event.stopPropagation()
      const mentionId = mention.getAttribute('data-id')
      if (!mentionId) {
        return
      }
      if (mention.getAttribute(MENTION_DISABLED_ATTR) === 'true') {
        const isDeleted = deletedMentionIds?.has(mentionId) ?? false
        const isVerifying = previewLoadingRef.current && tenantMemberIdRef.current === mentionId
        // Same gate as click: block only deleted members and in-flight verifications.
        // A gated-but-never-verified mention (e.g. a self-mention, whose preview is
        // intentionally skipped) still navigates — keyboard parity with mouse.
        if (isDeleted || isVerifying) {
          return
        }
      }
      onNavigate(mentionId)
    },
    [getMentionFromEvent, onNavigate, deletedMentionIds],
  )

  // The single preview member fetch, feeding both preview paths: the rich profile
  // card and the legacy popper (which receives this member as a prop). Only the
  // embeds the active path actually renders are requested. Skips the 404 interceptor
  // so a deleted member is handled inline (via handleMemberDeleted) instead of
  // routing to the global NotFound page.
  const [member, isFetched, isMemberNotFound] = useTenantMember(
    tenantMemberId ?? '',
    getPreviewEmbeds(richPreview),
    open && Boolean(tenantMemberId),
    true,
  )

  useEffect(() => {
    // Mirror the preview fetch state onto the mention's loading gate.
    setPreviewLoading(open && !isFetched)
  }, [open, isFetched, setPreviewLoading])

  useEffect(() => {
    // Once the fetch settles: a 404, or a member that resolved but isn't previewable
    // (suspended / soft-deleted), is gated like a deletion — mark it and close,
    // matching the avatar hover. A non-404 transient error (member stays null)
    // leaves the mention interactive.
    if (!open || !tenantMemberId || !isFetched) {
      return
    }
    if (isMemberNotFound || (member && !isPreviewableTenantMember(member))) {
      handleMemberDeleted(tenantMemberId)
    }
  }, [open, tenantMemberId, isFetched, isMemberNotFound, member, handleMemberDeleted])

  useLayoutEffect(() => {
    // Give each mention button semantics + an accessible label, and start it
    // navigation-disabled until a preview fetch verifies the member resolves.
    const container = containerRef.current
    if (!container) {
      return
    }
    const mentions = container.querySelectorAll<HTMLElement>('span[data-type="mention"][data-id]')
    mentions.forEach(mention => {
      if (mention.getAttribute(MENTION_A11Y_ATTR)) {
        return
      }
      mention.setAttribute(MENTION_A11Y_ATTR, 'true')
      mention.setAttribute('tabindex', '0')
      mention.setAttribute('role', 'button')
      const labelName = mention.textContent?.trim() ?? ''
      mention.setAttribute(
        'aria-label',
        tRef.current('View profile for {name}', { name: labelName }),
      )
      // Disabled until a preview fetch verifies the member still resolves.
      // Cleared on a successful preview (see the preview effect below).
      mention.setAttribute(MENTION_DISABLED_ATTR, 'true')
    })

    return () => {
      container
        .querySelectorAll<HTMLElement>(`span[data-type="mention"][${MENTION_A11Y_ATTR}]`)
        .forEach(mention => {
          mention.removeAttribute('tabindex')
          mention.removeAttribute('role')
          mention.removeAttribute('aria-label')
          mention.removeAttribute(MENTION_A11Y_ATTR)
          mention.removeAttribute(MENTION_DISABLED_ATTR)
          mention.removeAttribute('aria-describedby')
          mention.removeAttribute('aria-expanded')
          mention.removeAttribute('aria-busy')
        })
    }
  }, [htmlContent])

  useLayoutEffect(() => {
    // Permanently disable mentions whose members are known-deleted: drop the
    // button role/focusability (which also drives the default-cursor styling)
    // and keep navigation gated.
    const container = containerRef.current
    if (!container || !deletedMentionIds || deletedMentionIds.size === 0) {
      return
    }
    deletedMentionIds.forEach(id => {
      container
        .querySelectorAll<HTMLElement>(`span[data-type="mention"][data-id="${id}"]`)
        .forEach(mention => {
          mention.setAttribute(MENTION_DISABLED_ATTR, 'true')
          mention.removeAttribute('role')
          mention.removeAttribute('tabindex')
          mention.removeAttribute('aria-label')
        })
    })
  }, [htmlContent, deletedMentionIds])

  useLayoutEffect(() => {
    // Wire the active mention to its preview region for screen readers, and drive
    // the navigation gate: keep it disabled while the fetch is in flight, clear it
    // once the member is verified.
    const prev = lastPreviewAnchorRef.current
    // Closing or switching away only clears the aria/busy state. Do NOT clear the
    // navigation gate here: it is removed solely once a fetch has verified the
    // member (the success branch below). Clearing it on close would wrongly
    // re-enable a mention whose fetch was still in flight when the user left,
    // letting a later click reach a possibly-deleted profile.
    if (prev && prev !== anchorEl) {
      clearPreviewAria(prev)
    }
    const isDeleted = tenantMemberId ? deletedMentionIds?.has(tenantMemberId) : false
    if (open && anchorEl && tenantMemberId && !isDeleted) {
      anchorEl.setAttribute('aria-expanded', 'true')
      if (previewLoading) {
        anchorEl.setAttribute('aria-busy', 'true')
        // The preview region isn't rendered while loading (the skeleton and the
        // legacy popper render no matching id), so don't point aria-describedby at
        // a missing element yet — aria-busy is the loading signal.
        anchorEl.removeAttribute('aria-describedby')
        // Block navigation until the preview fetch settles.
        anchorEl.setAttribute(MENTION_DISABLED_ATTR, 'true')
      } else {
        // Fetch settled and the member resolved (a deleted member would have
        // isDeleted set and skip this branch), so it is verified: allow clicks and
        // point at the now-rendered preview region.
        anchorEl.setAttribute('aria-describedby', getMentionPreviewRegionId(tenantMemberId))
        anchorEl.removeAttribute('aria-busy')
        anchorEl.removeAttribute(MENTION_DISABLED_ATTR)
      }
      lastPreviewAnchorRef.current = anchorEl
    } else {
      if (lastPreviewAnchorRef.current) {
        clearPreviewAria(lastPreviewAnchorRef.current)
      }
      lastPreviewAnchorRef.current = null
    }
  }, [open, anchorEl, tenantMemberId, previewLoading, deletedMentionIds, clearPreviewAria])

  useLayoutEffect(() => {
    // Escape closes an open preview.
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }
      if (!openRef.current) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      closePreview()
    }
    document.addEventListener('keydown', onDocumentKeyDown)
    return () => document.removeEventListener('keydown', onDocumentKeyDown)
  }, [closePreview])

  useEffect(() => {
    // Delegate mention interactions from the container (mentions are raw HTML).
    const container = containerRef.current
    if (!container) {
      return
    }
    container.addEventListener('mouseover', handleMouseOver)
    container.addEventListener('click', handleMouseClick)
    container.addEventListener('focusin', handleFocusIn)
    container.addEventListener('focusout', handleFocusOut)
    container.addEventListener('keydown', handleKeyDown)
    return () => {
      container.removeEventListener('mouseover', handleMouseOver)
      container.removeEventListener('click', handleMouseClick)
      container.removeEventListener('focusin', handleFocusIn)
      container.removeEventListener('focusout', handleFocusOut)
      container.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleMouseOver, handleMouseClick, handleFocusIn, handleFocusOut, handleKeyDown])

  return {
    containerRef,
    anchorEl,
    tenantMemberId,
    open,
    closePreview,
    member,
    isFetched,
    deletedMentionIds,
  }
}
